<script>
import Vue from 'vue'
import { AgGridVue } from 'ag-grid-vue'

import UnionRulesetResource from '../../resources/unionRuleset'
import AgreementResource from '../../resources/agreement'
import JobTitleResource from '../../resources/jobTitle'
import UnionLocalResource from '../../resources/unionLocal'
import UnionJurisdictionResource from '../../resources/unionJurisdiction'
import OnProductionResource from '../../resources/onProduction'
import ScheduleResource from '../../resources/schedule'
import OccCodeResource from '../../resources/occCode'
import GridAutocomplete from '../GridAutocomplete.vue'
import toastMixin from '../../mixins/toast'

export default Vue.extend({
    name: 'UnionRulesetGrid',
    components: {
        AgGridVue,
    },
    mixins: [toastMixin],
    props: {
        rowData: {
            required: false,
            type: Array,
            default: () => {
                return []
            },
        },
    },
    data() {
        return {
            unionOccCodes: [],
            gridApi: undefined,
            columnApi: undefined,
            unionRulesetColumnDefs: [
                {
                    field: 'id',
                    headerName: 'ID#',
                    minWidth: 40,
                    maxWidth: 70,
                    sortable: true,
                    valueSetter: () => null,
                    valueGetter: (params) => {
                        return params.data.id
                    },
                },
                {
                    field: 'agreement_id',
                    headerName: 'Agreement',
                    width: 100,
                    minWidth: 50,
                    maxWidth: 400,
                    sortable: true,
                    editable: true,
                    valueGetter: (params) => {
                        return params.data.get('agreement_id.name')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: AgreementResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'union_local_id',
                    headerName: 'Local',
                    width: 70,
                    minWidth: 40,
                    maxWidth: 120,
                    sortable: true,
                    editable: true,
                    valueGetter: (params) => {
                        return params.data.get('union_local_id.code')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: UnionLocalResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'job_title_id',
                    headerName: 'Position',
                    minWidth: 150,
                    maxWidth: 300,
                    sortable: true,
                    editable: true,
                    valueGetter: (params) => {
                        return params.data.get('job_title_id.name')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: JobTitleResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'occCode_id',
                    headerName: 'OccCode',
                    width: 100,
                    minWidth: 60,
                    maxWidth: 100,
                    editable: true,
                    sortable: true,
                    cellRenderer: (params) => {
                        return params.value
                    },
                    valueGetter: (params) => {
                        let occ = this.getOccCode(params)
                        return occ && occ && occ.attributes ? occ.attributes.code : ''
                    },
                    valueSetter: (params) => {
                        let occ = this.getOccCode(params)

                        // Create new Occ code if one does not exist!
                        if (!occ) occ = this.addOccCode(params)

                        occ.set('code', params.newValue)
                        // Update all rows with a related OccCodeResource
                        setTimeout(() => this.gridApi.redrawRows(), 500)
                    },
                },
                {
                    field: 'schedule_id',
                    headerName: 'Schedule',
                    width: 120,
                    minWidth: 80,
                    maxWidth: 300,
                    editable: true,
                    sortable: true,
                    valueGetter: (params) => params.data.get('schedule_id.name'),
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: ScheduleResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'on_prod_id',
                    headerName: 'On/Off Prod',
                    minWidth: 40,
                    maxWidth: 150,
                    editable: true,
                    sortable: true,
                    valueGetter: (params) => params.data.get('on_prod_id.name'),
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: OnProductionResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'jurisdiction_id',
                    headerName: 'Jurisdiction',
                    width: 100,
                    minWidth: 50,
                    maxWidth: 200,
                    editable: true,
                    sortable: true,
                    valueGetter: (params) => params.data.get('jurisdiction_id.name'),
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: UnionJurisdictionResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'studio_rule_collection_id',
                    headerName: 'Studio',
                    width: 60,
                    sortable: true,
                    editable: true,
                    cellClass: 'amount editable',
                    valueSetter: (params) => {
                        params.data.set('studio_rule_collection_id', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('studio_rule_collection_id')
                    },
                },
                {
                    field: 'report_rule_collection_id',
                    headerName: 'Report',
                    width: 60,
                    sortable: true,
                    editable: true,
                    cellClass: 'amount editable',
                    valueSetter: (params) => {
                        params.data.set('report_rule_collection_id', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('report_rule_collection_id')
                    },
                },
                {
                    field: 'bus_rule_collection_id',
                    headerName: 'Bus',
                    width: 60,
                    sortable: true,
                    editable: true,
                    cellClass: 'amount editable',
                    valueSetter: (params) => {
                        params.data.set('bus_rule_collection_id', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('bus_rule_collection_id')
                    },
                },
                {
                    field: 'distant_rule_collection_id',
                    headerName: 'Distant',
                    width: 60,
                    sortable: true,
                    editable: true,
                    cellClass: 'amount editable',
                    valueSetter: (params) => {
                        params.data.set('distant_rule_collection_id', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('distant_rule_collection_id')
                    },
                },
                {
                    field: 'distant_studio_rule_collection_id',
                    headerName: 'Dist Studio',
                    width: 60,
                    sortable: true,
                    editable: true,
                    cellClass: 'amount editable',
                    valueSetter: (params) => {
                        params.data.set('distant_studio_rule_collection_id', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('distant_studio_rule_collection_id')
                    },
                },
                {
                    field: 'minimum_call_hours',
                    headerName: 'Min Call',
                    width: 70,
                    sortable: true,
                    editable: true,
                    cellClass: 'editable',
                    valueSetter: (params) => {
                        params.data.set('minimum_call_hours', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('minimum_call_hours')
                    },
                },
                {
                    field: 'distant_minimum_call_hours',
                    headerName: 'Dist Min Call',
                    width: 70,
                    sortable: true,
                    editable: true,
                    cellClass: 'editable',
                    valueSetter: (params) => {
                        params.data.set('distant_minimum_call_hours', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('distant_minimum_call_hours')
                    },
                },
                {
                    field: 'idle_minimum_call_hours',
                    headerName: 'Idle Call',
                    width: 70,
                    sortable: true,
                    editable: true,
                    cellClass: 'editable',
                    valueSetter: (params) => {
                        params.data.set('idle_minimum_call_hours', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('idle_minimum_call_hours')
                    },
                },
                {
                    field: 'date_start',
                    headerName: 'Start',
                    hide: true,
                    width: 70,
                    sortable: true,
                    editable: true,
                    cellClass: 'editable',
                    valueSetter: (params) => {
                        params.data.set('date_start', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('date_start')
                    },
                },
                {
                    field: 'date_end',
                    headerName: 'End',
                    hide: true,
                    width: 70,
                    sortable: true,
                    editable: true,
                    cellClass: 'editable',
                    valueSetter: (params) => {
                        params.data.set('date_end', params.newValue)
                    },
                    valueGetter: (params) => {
                        return params.data.get('date_end')
                    },
                },
                {
                    field: 'time_updated',
                    headerName: 'Updated',
                    hide: true,
                    width: 100,
                    sortable: true,
                    valueSetter: (params) => {},
                    valueGetter: (params) => {
                        return params.data.get('time_updated')
                    },
                },
                {
                    field: 'status',
                    headerName: 'Active',
                    width: 80,
                    editable: true,
                    sortable: true,
                    cellRenderer: (params) => {
                        let input = document.createElement('input')
                        input.type = 'checkbox'
                        input.checked = params.data.get('status') > 0 ? true : false
                        input.addEventListener('change', (event) => {
                            params.data.set('status', event.target.checked ? 1 : 0)
                        })
                        return input
                    },
                    valueGetter: (params) => {
                        return params.data.get('status')
                    },
                },
                {
                    field: 'agreement_id_ro',
                    headerName: 'Ag ID',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.agreement_id,
                },
                {
                    field: 'union_local_id_ro',
                    headerName: 'Loc ID',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.union_local_id,
                },
                {
                    field: 'job_title_id_ro',
                    headerName: 'Job ID',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.job_title_id,
                },
                {
                    field: 'schedule_id_ro',
                    headerName: 'Sch ID',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.schedule_id,
                },
                {
                    field: 'on_prod_id_ro',
                    headerName: 'OnOff ID',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.on_prod_id,
                },
                {
                    field: 'jurisdiction_id_ro',
                    headerName: 'Jurisdiction',
                    minWidth: 30,
                    maxWidth: 60,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.jurisdiction_id,
                },
            ],
            unionRulesetGridOptions: {
                enterMovesDown: true,
                enterMovesDownAfterEdit: true,
                defaultColDef: {
                    resizable: true,
                },
                frameworkComponents: {
                    gridAutocomplete: GridAutocomplete,
                },
                onGridReady: async (params) => {
                    if (params) {
                        this.gridApi = params.api
                        this.columnApi = params.columnApi
                    }
                    await this.getOccCodes()
                    this.gridApi.redrawRows()
                },

                onFirstDataRendered: (params) => {
                    params.api.sizeColumnsToFit()
                },
            },
        }
    },
    computed: {
        resources() {
            return {
                UnionRulesetResource,
                AgreementResource,
                UnionLocalResource,
                UnionJurisdictionResource,
                JobTitleResource,
                OnProductionResource,
                ScheduleResource,
            }
        },
    },
    methods: {
        selectUnionRuleset(params) {
            if (params && params.data && params.data.attributes) this.$emit('select', params.data)
        },

        async saveGrid() {
            let changes = false
            if (!this.rowData || !this.rowData.length) {
                this.createToast('b-toaster-top-center', 'warning', true, 'Warning', 'No Data to Save')
                return false
            }

            this.rowData.forEach(async (row) => {
                if (row.changes && Object.keys(row.changes).length) {
                    changes = true
                    row.validate()
                    try {
                        await row.save()
                        row.changes = {}
                    } catch (error) {
                        // console.log(error)
                        this.createToast('b-toaster-top-center', 'danger', true, 'Save Error', error, true)
                        throw error
                    }
                }
            })

            this.unionOccCodes.forEach(async (row) => {
                if (row.changes && Object.keys(row.changes).length) {
                    changes = true
                    row.validate()
                    try {
                        await row.save()
                        row.changes = {}
                    } catch (error) {
                        // console.log(error)
                        this.createToast('b-toaster-top-center', 'danger', true, 'Save Error', error, true)
                        throw error
                    }
                }
            })

            if (!changes) this.createToast('b-toaster-top-center', 'warning', true, 'Warning', 'No Data to Save')
            else this.createToast('b-toaster-top-center', 'success', true, 'Success', 'Finished Save')
        },

        async getOccCodes() {
            if (this.rowData) {
                const ags = [...new Set(this.rowData.map((row) => row.attributes.agreement_id))]
                if (!ags || ags.length === 0) return
                const response = await OccCodeResource.all({ agreement_id__in: ags })
                if (response && response.length) {
                    this.unionOccCodes = response
                    return response
                }
            } else return
        },

        getOccCode(params) {
            if (this.unionOccCodes.length) {
                return this.unionOccCodes.find(
                    (occ) =>
                        occ.attributes.agreement_id == params.data.attributes.agreement_id &&
                        occ.attributes.union_local_id == params.data.attributes.union_local_id &&
                        occ.attributes.job_title_id == params.data.attributes.job_title_id
                )
            } else {
                return
            }
        },

        addOccCode(params) {
            let occ = new OccCodeResource()
            occ.set('agreement_id', params.data.attributes.agreement_id)
            occ.set('union_local_id', params.data.attributes.union_local_id)
            occ.set('job_title_id', params.data.attributes.job_title_id)
            occ.set('status', 1)
            this.unionOccCodes.push(occ)
            return occ
        },

        exportGrid() {
            this.gridApi.exportDataAsExcel()
        },
    },

    async created() {},
})
</script>

<template>
    <div>
        <div class="left"><h1>Union Ruleset Grid</h1></div>
        <div class="right">
            <b-button variant="outline-success" @click="exportGrid">Export</b-button>
            <b-button variant="primary" @click="saveGrid">Save</b-button>
        </div>
        <div class="clear"></div>
        <ag-grid-vue class="ag-theme-balham default-grid" :grid-options="unionRulesetGridOptions" :column-defs="unionRulesetColumnDefs" :row-data="rowData" @row-clicked="selectUnionRuleset" />
        <div class="right">
            <b-button variant="outline-success" @click="exportGrid">Export</b-button>
            <b-button variant="primary" @click="saveGrid">Save</b-button>
        </div>
        <div class="clear"></div>
        <hr />
    </div>
</template>

<style>
@import '../../../node_modules/ag-grid-community/dist/styles/ag-grid.css';
@import '../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css';

.default-grid {
    width: 100%;
    height: 200px;
}

.clear {
    clear: both;
}

.left {
    float: left;
}

.right {
    float: right;
}

.amount {
    text-align: right;
}

.editable {
    background-color: #3ea2ff15;
}
</style>
